import { useAct } from 'FlowBuilder/store'
import './DonateActionForm.scoped.scss'
import ThankYouEmailSettingsForm from './Components/ThankYouEmailSettingsForm'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import currenciesData from 'FlowBuilder/lib/currenciesData'
import SortableSelect from 'Shared/components/SortableSelect'
import DonationAmounts from './Donate/DonationAmounts'
import { ActionTranslatableKeysDonations } from 'FlowBuilder/lib/actionTranslatable'
import validate from 'FlowBuilder/lib/donateValidator'
import { useDefaultFlowLanguage } from 'FlowBuilder/hooks/useLanguageFlow'

export default function DonationAmountsForm({ actionId }) {
  const act = useAct()

  const action = useSel(s => s.actions.entities[s.local.selectedActionId])
  const config = action.config || { amounts: [] }
  const currencies = config.currencies || window.ENV.SETTINGS.default_currencies
  const currencyOptions = currenciesData.map(c => ({ value: c.code, label: `${c.name} (${c.symbol})` }))
  const currencyValue = currencies.map(code => currencyOptions.find(c => c.value === code))
  const memoizedAction = useMemo(() => action, [JSON.stringify(action)]);
  const defaultLanguage = useDefaultFlowLanguage()

  function updateConfig(newConfig) {
    act.actions.upsert(actionId, { config: { ...config, ...newConfig } })
  }

  useEffect(() => {
    const errors = [];
    validate({ action: memoizedAction, defaultLanguage, setErrors: (validationErrors) => {
      errors.push(...validationErrors);
    } });
    act.builder.setValidationErrors(_.uniq(errors));

    return () => act.builder.setValidationErrors([]);
  }, [memoizedAction]);

  const {
    textContentUpdater, 
    translated: { 
      donateButtonText,
      donateButtonTextMonthly,
      monthlyUpsellText,
      monthlyUpsellButton,
      oneOffButton,
      coverFeeText,
      secureDonationText,
      giveOnceText,
      monthlyText,
      monthlyUpsellHeader,
      monthlyUpsellReturnText,
    },
    fallback: { 
      donateButtonText: donateButtonTextFallback,
      donateButtonTextMonthly: donateButtonTextMonthlyFallback,
      monthlyUpsellText: monthlyUpsellTextFallback, 
      monthlyUpsellButton: monthlyUpsellButtonFallback,
      oneOffButton: oneOffButtonFallback,
      coverFeeText: coverFeeTextFallback,
      secureDonationText: secureDonationTextFallback,
      giveOnceText: giveOnceTextFallback,
      monthlyText: monthlyTextFallback,
      monthlyUpsellHeader: monthlyUpsellHeaderFallback,
      monthlyUpsellReturnText: monthlyUpsellReturnTextFallback
    },
    flag
  } = useActionTranslate<ActionTranslatableKeysDonations>({ actionId })

  return (
    <div className="box narrow margin-bottom double">
      <h2 className='no-margin-top'>Donation currencies</h2>
      <div className='field donation-currencies-field'>
        <SortableSelect
          className="margin-top half"
          value={currencyValue}
          options={currencyOptions}
          onChange={selectedOptions => {
            if (!selectedOptions || selectedOptions.length === 0) return

            const selectedCurrencies = selectedOptions ? selectedOptions.map(option => option.value) : []
            updateConfig({ currencies: selectedCurrencies })
          }}
        />
        { currencyValue && currencyValue.length > 1 && <small>Drag and drop to change the order. The first currency will be the default currency used for donations, but the donor will automatically see a currency matching their country if one is available.</small> }
      </div>

      <DonationAmounts {...{ actionId }} />

      <h2 className='margin-top double'>Donation form settings</h2>


      <div className='flex field'>
        <label className="bold">Enable one-off donation option</label>
        <input
          className={"margin-left" + (config.disableOneOffDonations && !config.enableMonthlyDonations ? " error" : "")}
          type="checkbox"
          checked={!config.disableOneOffDonations}
          onChange={e => updateConfig({ disableOneOffDonations: !e.target.checked })}
        />
      </div>
      
      <div className='flex field margin-top'>
        <label className="bold">Enable monthly donation option</label>
        <input
          className={"margin-left" + (config.disableOneOffDonations && !config.enableMonthlyDonations ? " error" : "")}
          type="checkbox"
          checked={config.enableMonthlyDonations}
          onChange={e => updateConfig({ enableMonthlyDonations: e.target.checked })}
        />
      </div>
      {config.disableOneOffDonations && !config.enableMonthlyDonations && (
        <div className='error margin-top'>
          At least one donation type (one-off or monthly) must be enabled.
        </div>
      )}

      {config.enableMonthlyDonations && !config.disableOneOffDonations &&
        (<div className='flex field margin-top'>
          <label className="bold">Enable monthly upsell screen</label>
          <input
            className="margin-left"
            type="checkbox"
            checked={config.enableMonthlyUpsell}
            onChange={e => updateConfig({ enableMonthlyUpsell: e.target.checked })}
          />
        </div>
      )}

      <div className='flex field margin-top'>
        <label className="bold">Enable cover fee checkbox</label>
        <input
          className="margin-left"
          type="checkbox"
          checked={config.coverFee}
          onChange={e => updateConfig({ coverFee: e.target.checked, preTickCoverFee: false })}
        />
      </div>

      {config.coverFee && (
        <div className='flex field margin-top'>
          <label className="bold">Pre-tick cover fee checkbox</label>
          <input
            className="margin-left"
            type="checkbox"
            checked={config.preTickCoverFee}
            onChange={e => updateConfig({ preTickCoverFee: e.target.checked })}
          />
        </div>
      )}

      {config.enableMonthlyDonations && !config.disableOneOffDonations && config.enableMonthlyUpsell && config.coverFee && (
      <div className='tip margin-top'>
        <div className='icon'>
          <SvgIconsTip />
        </div>
        <p>When monthly upsell is enabled, the cover fee option will only appear for monthly donations. One-off donors will see the upsell screen instead.</p>
      </div>
      )}

      <h2 className='margin-top double'>Customise language</h2>


      {!config.disableOneOffDonations &&  (
        <div className='field margin-top'>
          <label className="bold dark-grey">Donate button text {flag}</label>
          <input
            className="margin-top half"
            type="text"
            value={donateButtonText ?? ''}
            placeholder={donateButtonTextFallback ?? ''}
            onChange={e => textContentUpdater('donateButtonText', e.target.value)}
          />
        </div>
      )}

      {config.enableMonthlyDonations &&  (
        <div className='field margin-top'>
          <label className="bold dark-grey">Donate monthly button text {flag}</label>
          <input
            className="margin-top half"
            type="text"
            value={donateButtonTextMonthly ?? ''}
            placeholder={donateButtonTextMonthlyFallback ?? ''}
            onChange={e => textContentUpdater('donateButtonTextMonthly', e.target.value)}
          />
        </div>
      )}

      {/* Only show top button selector text when both are enabled */}
      {config.enableMonthlyDonations && !config.disableOneOffDonations &&  (
        <>
          <div className='field margin-top'>
            <label className="bold dark-grey">Give once text {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={giveOnceText ?? ''}
              placeholder={giveOnceTextFallback ?? ''}
              onChange={e => textContentUpdater('giveOnceText', e.target.value)}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly text {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={monthlyText ?? ''}
              placeholder={monthlyTextFallback ?? ''}
              onChange={e => textContentUpdater('monthlyText', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Only show upsell when upsell and one-off donations are enabled */}
      {config.enableMonthlyUpsell && config.enableMonthlyDonations && !config.disableOneOffDonations && (
        <>
          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell header {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={monthlyUpsellHeader ?? ''}
              placeholder={monthlyUpsellHeaderFallback ?? ''}
              onChange={e => textContentUpdater('monthlyUpsellHeader', e.target.value)}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell text {flag}</label>
            <textarea
              className="margin-top half full-width"
              value={monthlyUpsellText ?? ''}
              placeholder={monthlyUpsellTextFallback ?? ''}
              onChange={e => textContentUpdater('monthlyUpsellText', e.target.value)}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell button text {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={monthlyUpsellButton ?? ''}
              placeholder={monthlyUpsellButtonFallback ?? ''}
              onChange={e => textContentUpdater('monthlyUpsellButton', e.target.value)}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell one-off button text {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={oneOffButton ?? ''}
              placeholder={oneOffButtonFallback ?? ''}
              onChange={e => textContentUpdater('oneOffButton', e.target.value)}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell return text {flag}</label>
            <input
              className="margin-top half"
              type="text"
              value={monthlyUpsellReturnText ?? ''}
              placeholder={monthlyUpsellReturnTextFallback ?? ''}
              onChange={e => textContentUpdater('monthlyUpsellReturnText', e.target.value)}
            />
          </div>
        </>
      )}

      {config.coverFee && (
        <div className='field margin-top'>
          <label className="bold dark-grey">Cover fee prompt {flag}</label>
          <textarea
            className="margin-top half full-width"
              value={coverFeeText ?? ''}
              placeholder={coverFeeTextFallback ?? ''}
              onChange={e => textContentUpdater('coverFeeText', e.target.value)}
          />
        </div>
      )}
      
      <div className='field margin-top'>
        <label className="bold dark-grey">Secure Donation text {flag}</label>
        <input
          className="margin-top half"
          type="text"
          value={secureDonationText ?? ''}
          placeholder={secureDonationTextFallback ?? 'Secure Donation'}
          onChange={e => textContentUpdater('secureDonationText', e.target.value)}
        />
      </div>
      
      <div className= "margin-top double">
        <ThankYouEmailSettingsForm actionId={actionId} additionalMergeTags={{
          'This Donation': [
            {
              'featured': true,
              'slug': 'amount',
              'label': 'This Donation Amount',
              'description': 'The amount of this donation'
            },
            {
              'featured': true,
              'slug': 'is_regular_donation',
              'label': 'Is a Regular Donation',
              'description': 'Whether the donation is a monthly subscription or a one-off'
            }
          ]
        }} />
      </div>
    </div>
  )
}



